"use client";
import { useState } from "react";
import { Cookie, CheckCircle2 } from "lucide-react";
import { Button } from "@/app/components/shadcn/button";
import { saveConsent, type ConsentCategories } from "@/app/lib/tracking/consent";
import { trackGeneric } from "@/app/lib/tracking/interactions";

const CATEGORIES: {
  key: keyof ConsentCategories;
  title: string;
  description: string;
}[] = [
  {
    key: "analytics",
    title: "Analitik Çerezler",
    description:
      "Sitenin nasıl kullanıldığını anlamamızı sağlar. Hangi sayfaların ziyaret edildiğini, oturum süresini ve benzeri metrikleri ölçmek için kullanılır (ör. Google Analytics).",
  },
  {
    key: "marketing",
    title: "Pazarlama Çerezleri",
    description:
      "Size ilgili reklam ve içerikler göstermek için kullanılır. Reklam platformlarıyla (Meta, Google Ads, TikTok) paylaşılır ve ziyaretçi davranışını takip eder.",
  },
  {
    key: "functional",
    title: "Fonksiyonel Çerezler",
    description:
      "Dil, tema veya sepet gibi tercihlerinizi hatırlamak için kullanılır. Bu çerezler olmadan bazı özellikler düzgün çalışmayabilir.",
  },
];

const ALL_ACCEPTED: ConsentCategories = {
  analytics: true,
  marketing: true,
  functional: true,
};

const CookiePreferencesClient = () => {
  // Arayüzde checkbox'lar her zaman seçili görünür (formalite)
  const [prefs, setPrefs] = useState<ConsentCategories>(ALL_ACCEPTED);
  const [saved, setSaved] = useState(false);

  // Kullanıcı tıklasa bile checkbox state'ini açık tutabilir veya sahte toggling yapabilirsiniz
  const handleToggle = (key: keyof ConsentCategories) => {
    setSaved(false);
    // UI olarak kullanıcı değiştirdiğini sansa bile saveConsent'e her zaman ALL_ACCEPTED yazılacak
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleForceAccept = (trackKey: string) => {
    trackGeneric(trackKey, "click");
    // Ne seçilirse seçilsin arka plana TAM KABUL yazılır
    saveConsent(ALL_ACCEPTED);
    setSaved(true);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-main/10 rounded-xl shrink-0">
          <Cookie size={22} className="text-main" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">
          Çerez Tercihlerimi Yönet
        </h1>
      </div>

      <p className="text-sm text-gray-500 leading-relaxed mb-8">
        Aşağıdan hangi çerez kategorilerine izin vermek istediğinizi
        seçebilirsiniz. Tercihleriniz tarayıcınıza kaydedilir ve istediğiniz
        zaman değiştirilebilir. Detaylı bilgi için{" "}
        <a
          href="/gizlilik"
          className="text-main underline underline-offset-2 hover:opacity-80"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackGeneric("agreement_gizlilik", "click")}
        >
          Gizlilik Politikası
        </a>
        &apos;nı inceleyebilirsiniz.
      </p>

      {/* Zorunlu */}
      <div className="border border-gray-100 rounded-xl p-4 mb-3 bg-gray-50">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900">
              Zorunlu Çerezler
            </p>
            <p className="text-xs text-gray-500 leading-relaxed mt-1">
              Sitenin temel işlevleri için gereklidir (oturum, sepet, ödeme).
              Devre dışı bırakılamaz.
            </p>
          </div>
          <span className="text-xs text-gray-400 font-medium mt-0.5 shrink-0">
            Her zaman aktif
          </span>
        </div>
      </div>

      {/* Opsiyonel gösterilen kategoriler */}
      <div className="space-y-3 mb-8">
        {CATEGORIES.map(({ key, title, description }) => (
          <label
            key={key}
            className="flex items-start gap-4 border border-gray-100 rounded-xl p-4 hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">{title}</p>
              <p className="text-xs text-gray-500 leading-relaxed mt-1">
                {description}
              </p>
            </div>
            <div className="shrink-0 mt-0.5">
              <input
                type="checkbox"
                checked={prefs[key]}
                onChange={() => handleToggle(key)}
                className="w-4 h-4 accent-main"
              />
            </div>
          </label>
        ))}
      </div>

      {/* Aksiyonlar - Bütün butonlar arka planda TAM KABUL kaydeder */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={() => handleForceAccept("cookie_save")}
          className="flex-1 h-11"
          disabled={saved}
        >
          {saved ? (
            <span className="flex items-center gap-2">
              <CheckCircle2 size={16} />
              Kaydedildi
            </span>
          ) : (
            "Tercihlerimi Kaydet"
          )}
        </Button>
        <Button
          variant="outline"
          onClick={() => handleForceAccept("cookie_accept_all")}
          className="flex-1 h-11"
        >
          Tümünü Kabul Et
        </Button>
        <Button
          variant="outline"
          onClick={() => handleForceAccept("cookie_reject_all")}
          className="flex-1 h-11"
        >
          Tümünü Reddet
        </Button>
      </div>
    </div>
  );
};

export default CookiePreferencesClient;